// Library Methods
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { Direction } from "@mui/material/styles/adaptV4Theme";
import { PaletteMode } from "@mui/material";

// Utils
import { getLocale } from "../locale/localeHelper";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Brand tokens — kept in sync with the CSS variables defined in
// src/styles/tailwind/index.css (--color-max-blue / --color-skyblue / --color-base-dark)
const brand = {
  maxBlue: "#3179fe",
  skyBlue: "#35bcfd",
  baseDark: "#202338",
};

const setupTheme = (
  direction: Direction | undefined,
  language: keyof typeof priorityLanguageMap,
  mode: PaletteMode = "light"
) => {
  const isDark = mode === "dark";

  // Scrollbar colors track the page background so the thumb/track read as
  // part of the surface rather than a hardcoded light-mode-only grey.
  const scrollbar = isDark
    ? { track: "#12141C", thumb: "#8B94A180" }
    : { track: "#EEEFF0", thumb: "#5B6D7A80" };

  const themeInfo = {
    palette: {
      mode,
      primary: { main: brand.maxBlue },
      secondary: { main: brand.skyBlue },
      ...(isDark && {
        background: { default: "#12141C", paper: brand.baseDark },
        text: { primary: "#F5F6F8", secondary: "#AEB4BE" },
        divider: "#5B6D7A40",
      }),
    },
    typography: {
      fontFamily: "Verdana, sans-serif",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 750,
        md: 1300,
        lg: 1400,
        xl: 1700,
        xxl: 1850,
      },
    },
    components: {
      // MUI uppercases button labels by default; show them as written.
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none" as const,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: "inherit",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: "#db3131",
            "&$error": {
              color: "#db3131",
            },
          },
        },
      },
      // Force scrollbars to always render instead of only on hover/scroll --
      // macOS hides them by default when no mouse is connected (trackpad-only).
      // Chrome/Edge/Safari only switch to a permanent classic scrollbar
      MuiCssBaseline: {
        styleOverrides: {
          "@supports not selector(::-webkit-scrollbar)": {
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: `${scrollbar.thumb} ${scrollbar.track}`,
            },
          },
          "*::-webkit-scrollbar": {
            width: "12px",
            height: "12px",
          },
          "*::-webkit-scrollbar-track": {
            background: scrollbar.track,
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: scrollbar.thumb,
            borderRadius: "8px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        },
      },
    },
  };

  const theme = createTheme(
    {
      direction,
      ...themeInfo,
    },
    getLocale(language).mui
  );
  return responsiveFontSizes(theme);
};

export { setupTheme };
